import {
  CancelExamEventExamineeModal,
  EXAM_EVENT_ACTIONABLE_STATUSES,
  EXAM_EVENT_EXAMINEE_OVERRIDABLE_EMAIL_STATUSES,
  EXAM_EVENT_EXAMINEE_CANCELLABLE_STATUSES,
  EXAM_EVENT_EXAMINEE_REINSTATABLE_STATUSES,
  OverrideExamEventExamineeEmailModal,
  ReinstateExamEventExamineeModal,
  getReinstatedStatus,
} from '@exam-center/exam-event-examinees';
import { useExamEventDetails } from '@exam-center/exam-event-management';
import {
  type EKycVerificationStatus,
  type ExamEventExamineeCertificateStatus,
  type ExamEventExamineeStatus,
  type ExamineeRegistrationStatus,
  useExportExaminationCodeByExamEventExamineeIdLazyQuery,
  useResendEKycRequestEmailMutation,
} from '@exam-center/graphql';
import { APP_PERMISSIONS } from '@exam-center/permission';
import {
  ConfirmationModal,
  FileDownloadOutlinedIcon,
  ForwardToInboxOutlinedIcon,
  HistoryIcon,
  MoreMenuIconButton,
  NoAccountsOutlinedIcon,
  PersonOutlinedIcon,
  Text,
} from '@shared/components';
import { useDisclosure } from '@shared/hooks';
import { usePermission } from '@shared/permissions';
import { useSnackbar } from '@shared/snackbar';
import { triggerFileDownload } from '@shared/utils';
import { useCallback, useMemo } from 'react';

import {
  EKYC_RE_SENDABLE_EKYC_STATUSES,
  EKYC_RE_SENDABLE_EXAM_EVENT_EXAMINEE_STATUS,
  EKYC_RE_SENDABLE_REGISTRATION_STATUSES,
  PRINTABLE_EXAM_TYPES,
  PRINTABLE_REGISTRATION_STATUSES,
  RESEND_CERTIFICATE_STATUSES,
} from '../../constants';
import { useProctoringDetailsModal } from '../../providers';
import { ConfirmResendCertificateModal } from '../confirm-resend-certificate-modal';

interface ExamineeMonitorMoreActionMenuProps {
  examEventExamineeId: string;
  examEventExamineeSeatNumber: number | null;
  examEventExamineeStatus: ExamEventExamineeStatus;
  examEventExamineeEkycStatus: EKycVerificationStatus;
  certificateStatus: ExamEventExamineeCertificateStatus;
  examCourseCode: string;
  examEventExamineeRegistrationStatus: ExamineeRegistrationStatus;
  examEventSessionExamineesExamStartedAt: (Date | string | null)[];
}

export default function ExamineeMonitorMoreActionMenu(
  props: Readonly<ExamineeMonitorMoreActionMenuProps>,
) {
  const {
    examEventExamineeId,
    examEventExamineeSeatNumber,
    examEventExamineeStatus,
    examEventExamineeEkycStatus,
    certificateStatus,
    examCourseCode,
    examEventExamineeRegistrationStatus,
    examEventSessionExamineesExamStartedAt,
  } = props;
  const {
    examEvent: { registrationStatus, examType, id: examEventId, status: examEventStatus },
  } = useExamEventDetails();
  const { permissions } = usePermission();
  const { showSnackbar } = useSnackbar();
  const { setAction, close } = useProctoringDetailsModal();
  const [isOpen, { close: closeConfirmation, open }] = useDisclosure(false);
  const [
    isResendCertificateModalOpen,
    { close: closeResendCertificateModal, open: openResendCertificateModal },
  ] = useDisclosure(false);
  const [
    isOverrideEmailModalOpen,
    { close: closeOverrideEmailModal, open: openOverrideEmailModal },
  ] = useDisclosure(false);
  const [isCancelModalOpen, { close: closeCancelModal, open: openCancelModal }] =
    useDisclosure(false);
  const [isReinstateModalOpen, { close: closeReinstateModal, open: openReinstateModal }] =
    useDisclosure(false);

  const [resendEKycRequestEmail, { loading }] = useResendEKycRequestEmailMutation();

  const [exportExaminationCode] = useExportExaminationCodeByExamEventExamineeIdLazyQuery({
    variables: {
      exportExaminationCodeByExamEventExamineeIdInput: {
        examEventExamineeId,
      },
    },
    fetchPolicy: 'no-cache',
  });

  const showErrorSnackbar = useCallback(() => {
    showSnackbar({
      message: 'ไม่สามารถโหลดข้อมูลได้ กรุณาลองใหม่',
      variant: 'error',
    });
  }, [showSnackbar]);

  const showSuccessSnackbar = useCallback(() => {
    showSnackbar({
      message: 'ดาวน์โหลดใบแจ้งรหัสสอบสำเร็จ',
      variant: 'success',
    });
  }, [showSnackbar]);

  const handlePrintExaminationCode = useCallback(async () => {
    setAction({ action: 'print-examination-code-loading' });
    await exportExaminationCode({
      onCompleted: (data) => {
        const pdfUrl = data.ecExportExaminationCodeByExamEventExamineeId.url;
        if (pdfUrl) {
          triggerFileDownload(pdfUrl);
          showSuccessSnackbar();
        } else {
          showErrorSnackbar();
        }
      },
      onError: () => {
        showErrorSnackbar();
      },
    });
    close();
  }, [setAction, exportExaminationCode, showSuccessSnackbar, showErrorSnackbar, close]);

  const handlePrintExaminationCodeClick = useCallback(async () => {
    if (examEventExamineeSeatNumber === null) {
      setAction({
        action: 'confirm-print-examination-code',
        data: { handleSubmit: handlePrintExaminationCode },
      });
    } else {
      await handlePrintExaminationCode();
    }
  }, [examEventExamineeSeatNumber, handlePrintExaminationCode, setAction]);

  const handleResendEKycEmail = useCallback(async () => {
    await resendEKycRequestEmail({
      variables: {
        resendEKycRequestEmailInput: {
          examEventExamineeId,
        },
      },
      onCompleted: () => {
        showSnackbar({
          message: 'ส่งอีเมลยืนยันตัวตน (e-KYC) เรียบร้อยแล้ว',
          variant: 'success',
        });
        closeConfirmation();
      },
      onError: (error) => {
        showSnackbar({
          message: error.message || 'ไม่สามารถส่งอีเมลยืนยันตัวตน (e-KYC) ได้',
          variant: 'error',
        });
      },
    });
  }, [closeConfirmation, examEventExamineeId, resendEKycRequestEmail, showSnackbar]);

  const baseExamineeMonitorMoreActionMenuOptions = useMemo(
    () => [
      {
        icon: <FileDownloadOutlinedIcon sx={{ color: (theme) => theme.palette.grey[600] }} />,
        label: 'ดาวน์โหลดใบแจ้งรหัสสอบ',
        onClick: handlePrintExaminationCodeClick,
        requiredPermission: [APP_PERMISSIONS.PROCTORING.DETAILS.UPDATE],
        shouldDisplay:
          PRINTABLE_REGISTRATION_STATUSES.includes(registrationStatus) &&
          PRINTABLE_EXAM_TYPES.includes(examType),
      },
      {
        icon: <PersonOutlinedIcon sx={{ color: (theme) => theme.palette.grey[600] }} />,
        label: 'แก้ไขอีเมลผู้เข้าสอบ',
        onClick: openOverrideEmailModal,
        requiredPermission: [APP_PERMISSIONS.PROCTORING.DETAILS.UPDATE],
        shouldDisplay:
          EXAM_EVENT_EXAMINEE_OVERRIDABLE_EMAIL_STATUSES.includes(examEventExamineeStatus),
      },
      {
        icon: <PersonOutlinedIcon sx={{ color: (theme) => theme.palette.grey[600] }} />,
        label: 'ส่งอีเมลยืนยันตัวตน (e-KYC)',
        onClick: open,
        requiredPermission: [APP_PERMISSIONS.PROCTORING.DETAILS.UPDATE],
        shouldDisplay:
          EKYC_RE_SENDABLE_EKYC_STATUSES.includes(examEventExamineeEkycStatus) &&
          EKYC_RE_SENDABLE_REGISTRATION_STATUSES.includes(registrationStatus) &&
          EKYC_RE_SENDABLE_EXAM_EVENT_EXAMINEE_STATUS.includes(examEventExamineeStatus),
      },
      {
        icon: <ForwardToInboxOutlinedIcon sx={{ color: (theme) => theme.palette.grey[600] }} />,
        label: 'ส่งอีเมลหนังสือรับรองผลสอบ',
        onClick: openResendCertificateModal,
        requiredPermission: [APP_PERMISSIONS.PROCTORING.DETAILS.UPDATE],
        shouldDisplay: RESEND_CERTIFICATE_STATUSES.includes(certificateStatus),
      },
      {
        icon: <NoAccountsOutlinedIcon sx={{ color: (theme) => theme.palette.error.main }} />,
        label: 'ยกเลิกผู้เข้าสอบ',
        onClick: openCancelModal,
        requiredPermission: [APP_PERMISSIONS.PROCTORING.DETAILS.UPDATE],
        shouldDisplay:
          EXAM_EVENT_EXAMINEE_CANCELLABLE_STATUSES.includes(examEventExamineeStatus) &&
          EXAM_EVENT_ACTIONABLE_STATUSES.includes(examEventStatus),
        sx: { color: 'error.main', '&:hover': { bgcolor: 'primary.lighter' } },
      },
      {
        icon: <HistoryIcon sx={{ color: (theme) => theme.palette.grey[600] }} />,
        label: 'คืนสถานะผู้เข้าสอบ',
        onClick: openReinstateModal,
        requiredPermission: [APP_PERMISSIONS.PROCTORING.DETAILS.UPDATE],
        shouldDisplay:
          EXAM_EVENT_EXAMINEE_REINSTATABLE_STATUSES.includes(examEventExamineeStatus) &&
          EXAM_EVENT_ACTIONABLE_STATUSES.includes(examEventStatus) &&
          getReinstatedStatus(
            examEventExamineeRegistrationStatus,
            examEventSessionExamineesExamStartedAt,
          ) !== null,
      },
    ],
    [
      examEventExamineeEkycStatus,
      examEventExamineeStatus,
      registrationStatus,
      examEventStatus,
      certificateStatus,
      handlePrintExaminationCodeClick,
      open,
      openResendCertificateModal,
      examType,
      openOverrideEmailModal,
      openCancelModal,
      openReinstateModal,
      examEventExamineeRegistrationStatus,
      examEventSessionExamineesExamStartedAt,
    ],
  );

  const examineeMonitorMoreActionMenuOptions = useMemo(
    () =>
      baseExamineeMonitorMoreActionMenuOptions.filter(
        (option) =>
          option.shouldDisplay &&
          option.requiredPermission.every((permission) => permissions.includes(permission)),
      ),
    [baseExamineeMonitorMoreActionMenuOptions, permissions],
  );

  if (examineeMonitorMoreActionMenuOptions.length === 0) {
    return null;
  }

  return (
    <>
      <MoreMenuIconButton options={examineeMonitorMoreActionMenuOptions} />
      <ConfirmationModal
        title='ยืนยันการส่งอีเมลยืนยันตัวตน (e-KYC)'
        content={
          <Text>
            คุณต้องการส่งอีเมลยืนยันตัวตน (e-KYC) <br />
            ไปยังผู้เข้าสอบรายนี้อีกครั้งใช่หรือไม่?
          </Text>
        }
        open={isOpen}
        acceptText='ยืนยัน'
        discardText='ยกเลิก'
        onDiscard={closeConfirmation}
        onClose={closeConfirmation}
        onAccept={handleResendEKycEmail}
        loading={loading}
      />
      <ConfirmResendCertificateModal
        open={isResendCertificateModalOpen}
        onClose={closeResendCertificateModal}
        examEventExamineeId={examEventExamineeId}
      />
      <OverrideExamEventExamineeEmailModal
        open={isOverrideEmailModalOpen}
        onClose={closeOverrideEmailModal}
        examEventId={examEventId}
        examEventExamineeId={examEventExamineeId}
      />
      <CancelExamEventExamineeModal
        open={isCancelModalOpen}
        onClose={closeCancelModal}
        examEventExamineeId={examEventExamineeId}
        examCourseCode={examCourseCode}
      />
      <ReinstateExamEventExamineeModal
        open={isReinstateModalOpen}
        onClose={closeReinstateModal}
        examEventExamineeId={examEventExamineeId}
        examCourseCode={examCourseCode}
      />
    </>
  );
}
